<template>
  <div class="wrapper-inside text-left usercv">
    <b-alert
      variant="warning"
      :show="!isEmploymentFilled || !isDocumentFilled"
      class="row"
    >
      <div class="col-12 col-md-1 m-auto">
        <font-awesome-icon
          icon="exclamation-triangle"
          class="danger-icon m-auto"
        />
      </div>
      <div class="col">
        {{ $t('pages.user-cv.fill-cv-pretext', 'In order to apply to job offers you must complete the necessary information') }}
        <ul>
          <li
            v-if="!isDocumentFilled"
            class="step"
            @click="documentModalOpen = true;"
          >
            {{ $t('pages.user-cv.upload-cv', 'Upload a CV document') }}
          </li>
          <li
            v-if="!isEmploymentFilled"
            class="step"
            @click="levelsModalOpen = true;"
          >
            {{ $t('pages.user-cv.fill-employment-status', 'Fill in your employment status') }}
          </li>
        </ul>
      </div>
    </b-alert>
    <b-alert
      variant="success"
      :show="isEmploymentFilled && isDocumentFilled"
    >
      {{ $t('pages.user-cv.profile-complete-note', 'Nice job! You can now apply for job offers.') }}
      <div>
        <b-button
          size="sm"
          variant="success"
          @click="$router.push({ name: 'search-offers', query: { place: 'Todas Ofertas', keyword: '' } })"
        >
          {{ $t('pages.user-cv.search-for-offers', 'Search for job offers now') }}
        </b-button>
      </div>
    </b-alert>

    <div class="bg-card">
      <h1>
        {{
          $t(
            'pages.user-cv.my-cv',
            'My CV {first} {last}',
            {
              first: $auth.user().first_name,
              last: $auth.user().last_name,
            }
          )
        }}
      </h1>
    </div>

    <div
      v-if="cv"
      class="row flex-xl-row"
    >
      <div class="col-12 col-xl-9">
        <PersonalData />
        <Employment
          v-if="cv.levels || cv.experience"
          :levels="cv.levels"
          :experience-time="cv.experience_time"
          :categories="cv.categories"
          :industries="cv.industries"
          :contracts="cv.contracts"
          @levelsModalOpen="levelsModalOpen = true;"
        />
        <Languages
          v-if="cv.languages"
          :languages="cv.languages"
          @langModalOpen="langModalOpen = true;"
        />
        <Document
          v-if="cv.document"
          :document="{ document: cv.document, created_at: cv.document_update_at }"
          @documentModalOpen="documentModalOpen = true;"
        />
        <Skills
          v-if="cv.skills"
          :skills="cv.skills"
          @skillsModalOpen="skillsModalOpen = true;"
        />
        <TextCv
          v-if="cv.text_cv"
          :text-cv="cv.text_cv"
          @textCvModalOpen="textCvModalOpen = true;"
        />
        <Educations
          v-if="cv.educations"
          :educations="cv.educations"
          @educationsModalOpen="educationsModalOpen = true;"
        />
        <Experience
          v-if="cv.experience"
          :experience="cv.experience"
          @experienceModalOpen="experienceModalOpen = true;"
        />
        <Recommendations
          v-if="cv.recommendations"
          :recommendations="cv.recommendations"
          @recommendedByModalOpen="recommendedByModalOpen = true;"
        />
      </div>
      <div class="col-12 col-xl-3 pl-lg-0">
        <Sidebar
          :cv="cv"
          @langModalOpen="langModalOpen = true;"
          @levelsModalOpen="levelsModalOpen = true;"
          @categoriesModalOpen="categoriesModalOpen = true;"
          @contractsModalOpen="contractsModalOpen = true;"
          @skillsModalOpen="skillsModalOpen = true;"
          @textCvModalOpen="textCvModalOpen = true;"
          @educationsModalOpen="educationsModalOpen = true"
          @experienceModalOpen="experienceModalOpen = true"
          @documentModalOpen="documentModalOpen = true"
          @recommendedByModalOpen="recommendedByModalOpen = true;"
        />
        <Advertisement v-if="mainCardsSize >= 3" />
        <FindCourses v-if="mainCardsSize >= 4" />
        <Internships v-if="mainCardsSize >= 5 || mainCardsSize >= 7" />
      </div>
    </div>
    <div v-if="cv">
      <LanguageModal
        :is-open="langModalOpen"
        :languages="cv.languages ? JSON.parse(cv.languages): []"
        @closeModal="langModalOpen = false"
        @updateCv="updateCv"
      />
      <EmploymentModal
        :is-open="levelsModalOpen"
        :levels="cv.levels"
        :experience="cv.experience_time"
        :categories="cv.categories"
        :industries="cv.industries"
        :contracts="cv.contracts"
        @closeModal="levelsModalOpen = false"
        @updateCv="updateCv"
      />
      <SkillsModal
        :is-open="skillsModalOpen"
        :skills="cv.skills"
        @closeModal="skillsModalOpen = false"
        @updateCv="updateCv"
      />
      <TextCvModal
        :is-open="textCvModalOpen"
        :text-cv="cv.text_cv"
        @closeModal="textCvModalOpen = false"
        @updateCv="updateCv"
      />
      <EducationModal
        :is-open="educationsModalOpen"
        :educations="cv.educations ? JSON.parse(cv.educations): []"
        @closeModal="educationsModalOpen = false"
        @updateCv="updateCv"
      />
      <ExperienceModal
        :is-open="experienceModalOpen"
        :experience="cv.experience ? JSON.parse(cv.experience): []"
        @closeModal="experienceModalOpen = false"
        @updateCv="updateCv"
      />
      <DocumentModal
        :is-open="documentModalOpen"
        :document="{ document: cv.document, created_at: cv.document_update_at }"
        @closeModal="documentModalOpen = false"
        @updateCv="updateCv"
      />
      <RecommendationsModal
        :is-open="recommendedByModalOpen"
        :recommendations="cv.recommendations ? JSON.parse(cv.recommendations): []"
        @closeModal="recommendedByModalOpen = false"
        @updateCv="updateCv"
      />
    </div>
  </div>
</template>

<script>
import PersonalData from '@/components/jobs/CV/PersonalData';
import Employment from '@/components/jobs/CV/Employment';
import Languages from '@/components/jobs/CV/Languages';
import Skills from '@/components/jobs/CV/Skills';
import TextCv from '@/components/jobs/CV/TextCv';
import Educations from '@/components/jobs/CV/Educations';
import Experience from '@/components/jobs/CV/Experience';
import Document from '@/components/jobs/CV/Document';
import Recommendations from '@/components/jobs/CV/MyRecommendations';
import Sidebar from '@/components/jobs/CV/Sidebar';

import LanguageModal from '@/components/jobs/CV/modals/Language';
import EmploymentModal from '@/components/jobs/CV/modals/Employment';
import SkillsModal from '@/components/jobs/CV/modals/Skills';
import TextCvModal from '@/components/jobs/CV/modals/TextCv';
import EducationModal from '@/components/jobs/CV/modals/Education';
import ExperienceModal from '@/components/jobs/CV/modals/Experience';
import DocumentModal from '@/components/jobs/CV/modals/Document';
import RecommendationsModal from '@/components/jobs/CV/modals/MyRecommendations';

import Advertisement from '@/components/jobs/infoSidebar/Advertisement';
import Internships from '@/components/jobs/infoSidebar/Internships.vue';
import FindCourses from '@/components/jobs/infoSidebar/FindCourses.vue';

export default {
  components: {
    PersonalData,
    Employment,
    Languages,
    Skills,
    TextCv,
    Educations,
    Experience,
    Recommendations,
    Document,
    Sidebar,

    LanguageModal,
    EmploymentModal,
    SkillsModal,
    TextCvModal,
    EducationModal,
    ExperienceModal,
    DocumentModal,
    RecommendationsModal,

    Advertisement,
    Internships,
    FindCourses
  },
  data() {
    return {
      isFirstLogin: false,
      cv: null,

      langModalOpen: false,
      levelsModalOpen: false,
      skillsModalOpen: false,
      textCvModalOpen: false,
      educationsModalOpen: false,
      experienceModalOpen: false,
      documentModalOpen: false,
      recommendedByModalOpen: false,
    }
  },
  computed: {
    isEmploymentFilled() {
      if(this.cv) {
         return this.cv.experience_time && this.cv.categories &&
          this.cv.industries && this.cv.contracts;
      } else {
        return false;
      }
    },
    isDocumentFilled() {
      if(!this.cv) return false;
      return this.cv.document;
    },
    mainCardsSize() {
      let count = 0;
      if(this.cv) {
        this.isEmploymentFilled && (count += 2);
        this.isDocumentFilled && (count += 1);
        this.cv.educations && ( count += 1);
        this.cv.languages && ( count += 1);
        this.cv.skills && ( count += 1);
        this.cv.text_cv && ( count += 1);
      }  else {
        return 0;
      }
      return count;
    }
  },
  mounted() {
    this.getCv();
    if(this.$route.params.firstLogin) {
      this.isFirstLogin = true;
    }
  },
  methods: {
    getCv() {
      this.axios.get('/cv')
        .then(response => this.cv = response.data)
        .catch( () => {
          this.$toastSomethingWentWrong();
        });
    },
    updateCv(data) {
      this.cv = data;
    }
  }
}
</script>


<style scoped lang="scss">
  .usercv {
    .bg-card {
      .subtext {
        font-size: 0.9rem;
      }
    }
  }

  .step {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
</style>

<style lang="scss">
  ul {
    margin-bottom: 0;

    li {
      padding: 0;
    }
  }
</style>
